import { useState, useMemo } from 'react';
import { useInventory } from '@/contexts/InventoryContext';
import { useApp } from '@/contexts/AppContext';
import type { Vendor } from '@/mocks/data';

export default function InventoryVendors() {
  const { vendors, transactions, items, addVendor, updateVendor, deleteVendor } = useInventory();
  const { addToast } = useApp();

  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [formData, setFormData] = useState({ name: '', contact: '', address: '', gstin: '', payment_terms: 'Net 15 days' });

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return vendors.filter((v) => !search || v.name.toLowerCase().includes(q) || v.gstin.toLowerCase().includes(q));
  }, [vendors, search]);

  const handleSave = () => {
    if (!formData.name) { addToast('error', 'Vendor name is required'); return; }
    if (editing) {
      updateVendor(editing.id, formData);
      addToast('success', `"${formData.name}" updated`);
    } else {
      const newVendor: Vendor = {
        id: `vnd-${Date.now()}`,
        ...formData,
        created_at: new Date().toISOString(),
      };
      addVendor(newVendor);
      addToast('success', `"${formData.name}" added as vendor`);
    }
    setShowForm(false);
    setEditing(null);
    setFormData({ name: '', contact: '', address: '', gstin: '', payment_terms: 'Net 15 days' });
  };

  const openEdit = (vendor: Vendor) => {
    setEditing(vendor);
    setFormData({ name: vendor.name, contact: vendor.contact, address: vendor.address, gstin: vendor.gstin, payment_terms: vendor.payment_terms });
    setShowForm(true);
  };

  const handleDelete = (id: string) => {
    const vendor = vendors.find((v) => v.id === id);
    const usedBy = transactions.filter((t) => t.vendor_id === id).length;
    if (usedBy > 0) {
      addToast('error', `Cannot delete "${vendor?.name}" — linked to ${usedBy} transactions`);
      return;
    }
    if (confirm(`Delete "${vendor?.name}"?`)) {
      deleteVendor(id);
      addToast('success', 'Vendor deleted');
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-navy-800">Vendors</h1>
          <p className="text-navy-500 mt-1">Supplier contacts, GST details, and transaction history.</p>
        </div>
        <button onClick={() => {
          setEditing(null);
          setFormData({ name: '', contact: '', address: '', gstin: '', payment_terms: 'Net 15 days' });
          setShowForm(true);
        }} className="flex items-center gap-2 px-4 py-2.5 bg-navy-700 text-white rounded-lg hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-add-line" /> Add Vendor
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Total Vendors</div>
          <div className="text-2xl font-bold text-navy-800">{vendors.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Total Purchases</div>
          <div className="text-2xl font-bold text-navy-800">{transactions.filter((t) => t.type === 'purchase').length}</div>
        </div>
        <div className="bg-white rounded-xl border border-navy-100 p-5">
          <div className="text-xs text-navy-500 mb-1">Purchase Value</div>
          <div className="text-2xl font-bold text-navy-800">Rs. {transactions.filter((t) => t.type === 'purchase').reduce((s, t) => s + (t.price || 0), 0).toLocaleString('en-IN')}</div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-navy-100 p-4 mb-6">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-navy-500 mb-1.5">Search</label>
          <div className="relative w-full max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
            <input type="text" placeholder="Name or GSTIN..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-9 pr-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((vendor) => {
          const vendorTxns = transactions.filter((t) => t.vendor_id === vendor.id && t.type === 'purchase');
          const totalSpent = vendorTxns.reduce((s, t) => s + (t.price || 0), 0);
          const name = vendor.name;
          const nameMap: Record<string, string> = {
            'Bharat Hygiene': 'bharat-hygiene',
            'Royal Linens': 'royal-linens',
            'Bright Electronics': 'bright-electronics',
            'Secure Hardware': 'secure-hardware',
            'Fresh Foods': 'fresh-foods',
          };
          const imgKey = nameMap[name] || name.toLowerCase().replace(/\s+/g, '-').slice(0, 20);
          return (
            <div key={vendor.id} className="bg-white rounded-xl border border-navy-100 p-5 hover:border-navy-200 transition-colors">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  <img
                    src={`https://readdy.ai/api/search-image?query=Minimalist%20corporate%20supplier%20company%20brand%20logo%20icon%20for%20$%7BencodeURIComponent%28vendor.name%29%7D%20business%2C%20clean%20white%20background%2C%20professional%20identity%20mark%2C%20simple%20geometric%20abstract%20symbol%2C%20flat%20design%20without%20shadows%2C%20neutral%20warm%20corporate%20colors%2C%20high%20resolution&width=200&height=200&seq=${imgKey}-logo&orientation=squarish`}
                    alt={vendor.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-navy-800">{vendor.name}</h3>
                  <div className="text-xs text-navy-500 mt-0.5">{vendor.contact}</div>
                  <div className="text-xs text-navy-400 mt-0.5 truncate">{vendor.address}</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-navy-500">GSTIN</span><span className="text-navy-700 font-mono text-xs">{vendor.gstin}</span></div>
                <div className="flex justify-between"><span className="text-navy-500">Terms</span><span className="text-navy-700 font-medium">{vendor.payment_terms}</span></div>
                <div className="flex justify-between"><span className="text-navy-500">Purchases</span><span className="text-navy-700 font-medium">{vendorTxns.length} orders</span></div>
                <div className="flex justify-between"><span className="text-navy-500">Total Spent</span><span className="text-navy-700 font-bold">Rs. {totalSpent.toLocaleString('en-IN')}</span></div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-navy-100">
                <button onClick={() => openEdit(vendor)} className="flex-1 px-3 py-2 text-xs font-medium text-navy-600 border border-navy-200 rounded-lg hover:bg-navy-50 transition-colors cursor-pointer text-center whitespace-nowrap">
                  <i className="ri-edit-line mr-1" />Edit
                </button>
                <button onClick={() => handleDelete(vendor.id)} className="flex-1 px-3 py-2 text-xs font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors cursor-pointer text-center whitespace-nowrap">
                  <i className="ri-delete-bin-line mr-1" />Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="px-6 py-4 border-b border-navy-100 flex items-center justify-between">
              <h3 className="font-semibold text-navy-800">{editing ? 'Edit Vendor' : 'Add Vendor'}</h3>
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-navy-400 hover:text-navy-600 cursor-pointer"><i className="ri-close-line text-xl" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Name *</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} /></div>
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Contact</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.contact} onChange={(e) => setFormData((p) => ({ ...p, contact: e.target.value }))} /></div>
              <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Address</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.address} onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">GSTIN</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.gstin} onChange={(e) => setFormData((p) => ({ ...p, gstin: e.target.value }))} /></div>
                <div><label className="block text-sm font-medium text-navy-700 mb-1.5">Payment Terms</label><input type="text" className="w-full px-3 py-2.5 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500" value={formData.payment_terms} onChange={(e) => setFormData((p) => ({ ...p, payment_terms: e.target.value }))} /></div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-navy-100 flex gap-3">
              <button onClick={() => { setShowForm(false); setEditing(null); }} className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer">Cancel</button>
              <button onClick={handleSave} className="flex-1 px-4 py-2.5 bg-navy-700 text-white rounded-lg text-sm font-medium hover:bg-navy-800 transition-colors cursor-pointer">{editing ? 'Save Changes' : 'Add Vendor'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}